import React from 'react'
import { graphql } from 'gatsby'
import MastHead from '../components/organisms/MastHead/masthead'
import GalleryCarousel from '../components/organisms/GalleryCarousel/GalleryCarousel'
import TypographyAtom from '../components/atoms/typography/typography'
import Breadcrumbs from '../components/organisms/Breadcrumbs/Breadcrumbs'
import { createArticleSchema } from '../helpers/createArticleSchema'
import { createWebpageSchema } from '../helpers/createWebpageSchema'
import ArticleAuthor from '../components/organisms/ArticleAuthor/ArticleAuthor'
import getOrderedRecommendedProductList from '../helpers/getOrderedRecommendedProductList'
import SectionHeader from '../components/organisms/SectionHeader/SectionHeader'
import ProductCard from '../components/organisms/ProductCard/ProductCard'
import ArticleCarousel from '../components/organisms/GalleryCarousel/ArticleCarousel'
import MetaTwitterCard from '../components/organisms/MetaTwitterCard/MetaTwitterCard'

// Gatsby Head
//---------------------------------
export function Head({ data, pageContext, location }) {
  const title = data.contentfulPageBlogArticle.pageMetaTitle
  const description = data.contentfulPageBlogArticle.pageMetaDescription
  const articleCategory = data?.contentfulPageBlogArticle?.articleCategory || ''
  const ogTitle = data?.contentfulPageBlogArticle?.articleTitle || title

  const isNewsArticle = articleCategory === 'News'

  const url = `${process.env.GATSBY_HELIGUY_DOMAIN}${location?.pathname}`

  const articleSchema = createArticleSchema(data.contentfulPageBlogArticle, url, isNewsArticle)
  const webPageSchema = createWebpageSchema({
    pageTitle: title,
    pageDescription: description,
    pageUrl: url,
    breadcrumbs: pageContext?.breadCrumbs,
  })

  const pageObjectUrl =
    data.contentfulPageBlogArticle.posterImage?.url || 'https://placehold.co/400x400'
  const pageObjectUrlWidth =
    data.contentfulPageBlogArticle.posterImage?.gatsbyImageData?.width || '400'
  const pageObjectUrlHeight =
    data.contentfulPageBlogArticle.posterImage?.gatsbyImageData?.height || '400'

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <html lang="en" />
      <meta charset="UTF-8" />
      <meta name="keywords" content="" />
      <meta name="author" content="Heliguy" />
      <meta property="og:locale" content="en" />
      <meta property="og:site_name" content="Heliguy™" />
      <meta property="og:type" content="article" />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={pageObjectUrl} />
      <meta property="og:image:width" content={pageObjectUrlWidth} />
      <meta property="og:image:height" content={pageObjectUrlHeight} />
      <meta
        property="og:url"
        content={`${process.env.GATSBY_HELIGUY_DOMAIN}${location.pathname}`}
      />
      <MetaTwitterCard
        title={ogTitle}
        mainImage={pageObjectUrl}
        pageUrl={`${process.env.GATSBY_HELIGUY_DOMAIN}${location.pathname}`}
        pageDescription={description}
      />
      <link rel="preconnect" href="https://images.ctfassets.net" crossOrigin />
      <link
        rel="canonical"
        href={`${process.env.GATSBY_HELIGUY_DOMAIN}${pageContext.breadcrumb.location}`}
      />
      {articleSchema && <script type="application/ld+json">{articleSchema}</script>}
      {webPageSchema && <script type="application/ld+json">{webPageSchema}</script>}
    </>
  )
}

// Page Export
//---------------------------------
function BlogArticleTemplate({ data, pageContext }) {
  const {
    breadcrumb: { crumbs },
  } = pageContext

  const articleCategory = data?.contentfulPageBlogArticle?.articleCategory || ''
  const authorName = data?.contentfulComponentStaffCard?.staffMemberName || ''
  const authorPhoto = data?.contentfulComponentStaffCard?.staffMemberPhoto?.gatsbyImageData
  const authorBiography =
    data?.contentfulComponentStaffCard?.staffMemberBiography?.staffMemberBiography || ''
  const relatedProductSectionTitle =
    data?.contentfulPageBlogArticle?.relatedProductsSectionTitle || ''
  const relatedProductSectionSubtitle =
    data?.contentfulPageBlogArticle?.relatedProductsSectionSubtitle || ''
  const relatedProductOrderList = data?.contentfulPageBlogArticle?.relatedProductList || []
  const relatedProductList = data?.allShopifyProduct?.edges || []

  const relatedProductOrderedList = getOrderedRecommendedProductList({
    productArray: relatedProductList.map(({ node }) => ({ ...node })),
    orderArray: relatedProductOrderList.map(({ productHandle }) => productHandle),
  })

  const isRelatedProducts =
    relatedProductSectionTitle &&
    relatedProductSectionSubtitle &&
    relatedProductOrderedList.length > 0

  const relatedArticles = data?.contentfulPageBlogArticle?.relatedArticles || []

  const publishedDate = data?.contentfulPageBlogArticle?.publishedDate || ''
  const updatedDate = data?.contentfulPageBlogArticle?.updatedDate || ''
  const snippetYouTubeLink = data?.contentfulPageBlogArticle?.snippetYouTubeLink
  const snippetVideoFile = data?.contentfulPageBlogArticle?.snippetVideoFile?.file?.url

  const isPartiallyActive = ({ isPartiallyCurrent, isCurrent }) =>
    isPartiallyCurrent && isCurrent
      ? { className: 'breadcrumb__link breadcrumb__link__active' }
      : {}

  const defineContentStyles = () => {
    if (isRelatedProducts) {
      return 'col-xs-12 col-md-8'
    }
    if (articleCategory !== 'Snippets') {
      return 'col-xs-12 col-md-9'
    }

    return 'col-xs-12'
  }

  return (
    <article
      className={`templateWrapper templateWrapper--alt ${authorName && !relatedArticles.length ? 'margin-author-padding' : ''}`}
    >
      <section className="outer">
        <div className="inner">
          <Breadcrumbs
            crumbs={pageContext.breadCrumbs}
            crumbSeparator=" - "
            crumbLabel={
              data.contentfulPageBlogArticle.breadcrumbTitle ??
              data.contentfulPageBlogArticle.pageMetaTitle
            }
            getProps={isPartiallyActive}
          />
          <MastHead
            titleField="articleTitle"
            subtitleField="articleSubtitle"
            imageField="posterImage"
            imgSrc={data.contentfulPageBlogArticle.posterImage?.gatsbyImageData}
            imgAltTag={data.contentfulPageBlogArticle.articleTitle}
            title={data.contentfulPageBlogArticle.articleTitle}
            subtitle={data.contentfulPageBlogArticle.articleSubtitle}
            type="Blog"
            articleCategory={articleCategory}
            articleAuthor={authorName}
            articleUpdatedDate={updatedDate}
            articlePublishedDate={publishedDate}
            snippetYouTubeLink={snippetYouTubeLink}
            snippetVideoFile={snippetVideoFile}
          />
        </div>
      </section>
      <section className="outer">
        <div className="inner">
          <div className="row between-md">
            <div className={defineContentStyles()}>
              <div
                className={`${(authorName && articleCategory !== 'Snippets') || isRelatedProducts ? 'margin-80' : 'margin-160'}`}
              >
                <TypographyAtom
                  type="BlogText"
                  copyBlock={data.contentfulPageBlogArticle.bodyCopy}
                  weight="BlockText"
                />
                {data.contentfulPageBlogArticle.imageGallery !== null && (
                  <div className="row">
                    <div
                      className={`col-xs-12 ${articleCategory === 'Snippets' ? 'col-md-7' : ''}`}
                    >
                      <GalleryCarousel
                        carouselImages={data.contentfulPageBlogArticle.imageGallery || []}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            {isRelatedProducts && (
              <div className="col-md-4 margin-80 margin-none-md">
                <div className="margin-24 margin-md-32">
                  <SectionHeader headerCopy={relatedProductSectionTitle} marginNone thin />
                </div>
                <ul className="list">
                  {relatedProductOrderedList &&
                    relatedProductOrderedList.map((product) => (
                      <li key={product.handle}>
                        <ProductCard
                          relatedProduct
                          productTitle={product.title}
                          productPrice={
                            product.priceRangeV2 === null
                              ? null
                              : product.priceRangeV2.minVariantPrice.amount
                          }
                          compareAtPrice={product.variants && product.variants[0]?.compareAtPrice}
                          isRental={product.productType.toLowerCase().includes('rental')}
                          isNotify={product.tags && product.tags.some((tag) => tag === 'notify')}
                          hidePrice={product?.tags?.includes('enquire only') || false}
                          imgSrc={product.featuredImage?.gatsbyImageData || ''}
                          altTag={product.featuredImage?.description || ''}
                          productLink={`/products/${product.handle}`}
                        />
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>
      {authorName && articleCategory !== 'Snippets' && (
        <section className={`outer ${relatedArticles.length > 0 ? 'margin-author-section' : ''}`}>
          <div className="inner flex">
            <ArticleAuthor
              authorName={authorName}
              authorPhoto={authorPhoto}
              authorBiography={authorBiography}
            />
          </div>
        </section>
      )}
      {relatedArticles.length > 0 && (
        <section className="outer">
          <div className="inner">
            <div className="margin-md-8 margin-lg-32">
              <SectionHeader headerCopy="Related Articles" large marginNone />
            </div>
            <ArticleCarousel carouselImages={relatedArticles} nonFooter />
          </div>
        </section>
      )}
    </article>
  )
}

export default BlogArticleTemplate

// GraphQL Call
//---------------------------------
export const data = graphql`
  query ($slug: String!, $articleAuthor: String, $relatedProductList: [String]) {
    contentfulPageBlogArticle(pageSlug: { eq: $slug }) {
      __typename
      contentful_id
      articleTitle
      articleSubtitle
      articleShortDescription
      articleCategory
      publishedDate
      updatedDate
      pageMetaDescription
      breadcrumbTitle
      pageMetaTitle
      pageSlug
      bodyCopy {
        raw
        references {
          ... on Node {
            ... on ContentfulAsset {
              __typename
              gatsbyImageData(width: 1920)
              contentful_id
              file {
                url
              }
            }
            ... on ContentfulPageProductInformation {
              __typename
              contentful_id
              pageSlug
            }
          }
        }
      }
      imageGallery {
        gatsbyImageData(width: 1920)
        url
      }
      posterImage {
        gatsbyImageData(width: 1920)
        url
      }
      snippetYouTubeLink
      snippetVideoFile {
        file {
          url
        }
      }
      updatedAt
      createdAt
      relatedProductsSectionTitle
      relatedProductsSectionSubtitle
      relatedProductList {
        ... on Node {
          ... on ContentfulPageProductInformation {
            productHandle
          }
          ... on ContentfulPageRentalProductInformation {
            productHandle
          }
        }
      }
      relatedArticles {
        id
        pageSlug
        articleTitle
        articleShortDescription
        articleCategory
        articleAuthor
        publishedDate
        updatedAt
        snippetYouTubeLink
        snippetVideoFile {
          file {
            url
          }
        }
        posterImage {
          url
          description
          gatsbyImageData(formats: WEBP, width: 1000)
        }
      }
    }
    contentfulComponentStaffCard(staffMemberName: { eq: $articleAuthor }) {
      staffMemberName
      staffMemberPhoto {
        gatsbyImageData(width: 768)
      }
      staffMemberBiography {
        staffMemberBiography
      }
    }
    allShopifyProduct(filter: { handle: { in: $relatedProductList } }) {
      edges {
        node {
          title
          shopifyId
          handle
          productType
          priceRangeV2 {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          variants {
            compareAtPrice
            price
          }
          featuredImage {
            gatsbyImageData(formats: WEBP, width: 900)
            altText
          }
          tags
        }
      }
    }
  }
`
